import {naturalSort} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React from 'react';

import {AuditLogFormat} from 'audit/AuditDAO';
import notifications from 'notifications';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import DatetimeField from 'ui-library/inputs/DatetimeField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import {isValidDatetime} from 'util/DateUtil';

import {
  ALL_EXPORT_AUDIT_LOG_MODAL_DATETIME_FORMATS,
  EXPORT_AUDIT_LOG_MODAL_DATETIME_FORMAT,
  EXPORT_AUDIT_LOG_MODAL_TITLE,
  LOCATION_HISTORY_TIME_RANGE_TOOLTIP,
} from './AuditConstants';

interface AuditLogModalRequestSubmitParams {
  format: AuditLogFormat;
  startTime?: string;
  endTime?: string;
}

export interface ExportAuditLogModalProps {
  onRequestSubmit: (params: AuditLogModalRequestSubmitParams) => void;
  onRequestClose: () => void;
  modalTitle?: string;
  dropdownDisplayString?: string;
  showDateRangeSelect?: boolean;
}

export default function ExportAuditLogModal(props: ExportAuditLogModalProps): JSX.Element {
  const [selectedFormat, setSelectedFormat] = React.useState<AuditLogFormat>(AuditLogFormat.PDF);
  const [exportStartDate, setExportStartDate] = React.useState<string>();
  const [exportEndDate, setExportEndDate] = React.useState<string>();

  return (
    <Modal
      enableRedesign={true}
      title={props.modalTitle}
      onRequestClose={props.onRequestClose}
      medium={true}
      scrollable={false}
      expandHeight={false}
      onRequestSubmit={() => {
        if (
          exportStartDate &&
          !isValidDatetime({
            textValue: exportStartDate,
            validDatetimeFormats: ALL_EXPORT_AUDIT_LOG_MODAL_DATETIME_FORMATS,
          })
        ) {
          notifications.error('Start date is not valid.');
        } else if (
          exportEndDate &&
          !isValidDatetime({
            textValue: exportEndDate,
            validDatetimeFormats: ALL_EXPORT_AUDIT_LOG_MODAL_DATETIME_FORMATS,
          })
        ) {
          notifications.error('End date is not valid.');
        } else if (
          (exportStartDate && moment(exportStartDate) > moment()) ||
          (exportEndDate && moment(exportEndDate) > moment())
        ) {
          notifications.error('Start or end date cannot be in the future.');
        } else if (exportStartDate && exportEndDate && exportStartDate > exportEndDate) {
          notifications.error('Start date cannot be after end date.');
        } else {
          props.onRequestSubmit({
            format: selectedFormat,
            startTime: exportStartDate ? moment(exportStartDate).format() : undefined, // Put into ISO format
            endTime: exportEndDate ? moment(exportEndDate).format() : undefined,
          });
        }
      }}
      buttons={[
        {
          text: 'Export',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: selectedFormat == null,
        },
      ]}
      dataTestAttrs={{component: 'ExportAuditLogModal', element: 'root'}}
    >
      <FormGroup>
        <FieldLabel label="Export format" />
        <SelectDropdown
          displayName={selectedFormat ?? props.dropdownDisplayString}
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
              {naturalSort(Object.keys(AuditLogFormat)).map((availableFormat) => (
                <Option key={availableFormat} onSelect={setSelectedFormat} value={availableFormat}>
                  {availableFormat}
                </Option>
              ))}
            </Menu>
          }
          dataTestAttrs={{component: 'ExportAuditLogModal', element: 'dropdown'}}
        />
      </FormGroup>
      {props.showDateRangeSelect && (
        <>
          <FieldLabel label="Export date range" />
          <Tooltip text={LOCATION_HISTORY_TIME_RANGE_TOOLTIP} placement={FloatingPlacement.RIGHT}>
            <BenchlingIcon icon="circle-question" className={css(styles.icon)} />
          </Tooltip>
          <FormGroup className={css(styles.dateTimeGroup)}>
            <DatetimeField
              className={css(styles.dateTimeField)}
              autoFocus={false}
              labelStyle={DatetimeField.LABEL_STYLES.SUBLABEL}
              label="Start date"
              placeholder="Date and time"
              onChange={setExportStartDate}
              value={exportStartDate}
              preventDefault={true}
              datetimePresentationalFormat={EXPORT_AUDIT_LOG_MODAL_DATETIME_FORMAT}
              validDatetimeInputFormats={ALL_EXPORT_AUDIT_LOG_MODAL_DATETIME_FORMATS}
            />
            <DatetimeField
              className={css(styles.dateTimeField)}
              autoFocus={false}
              labelStyle={DatetimeField.LABEL_STYLES.SUBLABEL}
              label="End date"
              placeholder="Date and time"
              onChange={setExportEndDate}
              value={exportEndDate}
              preventDefault={true}
              datetimePresentationalFormat={EXPORT_AUDIT_LOG_MODAL_DATETIME_FORMAT}
              validDatetimeInputFormats={ALL_EXPORT_AUDIT_LOG_MODAL_DATETIME_FORMATS}
            />
          </FormGroup>
        </>
      )}
    </Modal>
  );
}

ExportAuditLogModal.defaultProps = {
  modalTitle: EXPORT_AUDIT_LOG_MODAL_TITLE,
  dropdownDisplayString: 'Select audit log format...',
};

const styles = StyleSheet.create({
  dateTimeGroup: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  dateTimeField: {
    width: '210px',
    marginTop: '5px',
  },
  icon: {
    color: GRAY_DARK,
    marginLeft: 4,
  },
});
